import { useNavigate } from 'react-router-dom';
import { ArrowUpRight, Film, FolderInput, Upload } from 'lucide-react';
import { useT } from '../lib/i18n';
import { bytes, clock, qualityLabel } from '../lib/format';
import { thumbUrl } from '../lib/api';
import { CountUp, Gauge, MediaStatusChip } from '../ui';
import type { Media, Overview } from '../lib/types';

export function StorageCard({ ov }: { ov: Overview }) {
  const t = useT();
  const nav = useNavigate();
  const d = ov.drive;
  // With a library limit the gauge is the library against that limit; otherwise it's the whole
  // disk, split into the library, everything else, and what's free.
  const quota = d.quota > 0;
  const total = Math.max(1, d.total);
  const used = quota ? Math.min(1, d.used / Math.max(1, d.quota)) : Math.min(1, (total - d.free) / total);
  const seg = (n: number) => `${Math.max(0, (n / total) * 100)}%`;
  return (
    <section className="card" data-anim>
      <div className="card-head" style={{ alignItems: 'center', marginBottom: 14 }}>
        <h2 className="card-title" style={{ flex: 1 }}>
          {t('storage.title')}
        </h2>
        <button className="round-btn" style={{ background: 'var(--tile)', width: 34, height: 34 }} onClick={() => nav('/drive')} aria-label={t('nav.drive')}>
          <ArrowUpRight size={15} />
        </button>
      </div>
      <div className="gauge-box">
        <Gauge value={used} />
        <div className="gauge-center">
          <b>
            <CountUp value={used * 100} />
            <small>%</small>
          </b>
          <span>{quota ? 'of the library limit' : `of ${d.name} used`}</span>
        </div>
        <div className="gauge-foot">
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</span>
          <span className="mono" style={{ fontWeight: 500, fontSize: 11.5, flex: 'none', marginLeft: 8 }}>
            {quota ? `${bytes(d.used, 1)} / ${bytes(d.quota, 0)}` : `${bytes(d.free, 1)} free of ${bytes(d.total, 0)}`}
          </span>
        </div>
      </div>
      <div className="disk-bar" role="img" aria-label={`Library ${bytes(d.used, 1)}, other ${bytes(d.other, 0)}, free ${bytes(d.free, 1)}`}>
        <span className="lib" style={{ width: seg(d.used) }} />
        <span className="other" style={{ width: seg(d.other) }} />
      </div>
      <div className="disk-legend">
        <span>
          <i className="lib" /> Library <b>{bytes(d.used, 1)}</b>
        </span>
        <span>
          <i className="other" /> Other files <b>{bytes(d.other, 0)}</b>
        </span>
        <span>
          <i /> Free <b>{bytes(d.free, 1)}</b>
        </span>
      </div>
      <p className="muted" style={{ fontSize: 12, margin: '10px 0 0' }}>
        {d.available > 0
          ? `${bytes(d.available, 1)} can still be added${quota ? '' : ` (${bytes(d.reserve, 0)} stays free for the system)`}.`
          : quota
            ? 'The library limit is reached. Raise it in Settings → Server, or remove titles.'
            : `The disk is nearly full: ${bytes(d.reserve, 0)} is kept free for the system. Free up space or move the library to a bigger drive.`}
      </p>
    </section>
  );
}

/** One library row's title cell: thumbnail, clean title, and release details. */
export function TitleCell({ m }: { m: Media }) {
  const meta = m.meta ?? { title: m.title };
  const bits: string[] = [];
  if (meta.year) bits.push(String(meta.year));
  if (meta.season && meta.episode) bits.push(`S${meta.season} · E${meta.episode}${meta.episodeTitle ? ` · ${meta.episodeTitle}` : ''}`);
  const thumb = thumbUrl(m);
  return (
    <div className="title-cell">
      <div className="thumb" style={{ backgroundImage: thumb ? `url(${thumb})` : undefined, display: 'grid', placeItems: 'center' }}>
        {!thumb && <Film size={15} className="muted" />}
      </div>
      <div className="title-text">
        <div className="title-main" title={m.fileName}>
          {m.title}
        </div>
        <div className="title-meta">
          {bits.map((b) => (
            <span key={b}>{b}</span>
          ))}
          {(meta.tags ?? []).slice(0, 3).map((tg) => (
            <span key={tg} className="tag">
              {tg}
            </span>
          ))}
          {!!m.subtitles?.length && (
            <span className="tag tag-cc" title={`Subtitles: ${m.subtitles.map((s) => s.label).join(', ')}`}>
              CC {m.subtitles.length > 1 ? m.subtitles.length : ''}
            </span>
          )}
          {m.origin === 'watch-folder' ? <FolderInput size={12} aria-label="From the drive folder" /> : <Upload size={12} aria-label="Uploaded" />}
        </div>
      </div>
    </div>
  );
}

export function MediaTable({ rows, onOpen, actions }: { rows: Media[]; onOpen?: (m: Media) => void; actions?: (m: Media) => React.ReactNode }) {
  const t = useT();
  if (!rows.length) return <div className="empty">{t('drive.dropSub')}</div>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="table">
        <colgroup>
          <col style={{ width: '44%' }} />
          <col className="hide-sm" style={{ width: '22%' }} />
          <col />
          <col />
          <col />
          {actions && <col />}
        </colgroup>
        <thead>
          <tr>
            <th>{t('col.title')}</th>
            <th className="hide-sm">{t('col.file')}</th>
            <th>{t('col.quality')}</th>
            <th>{t('col.status')}</th>
            <th>{t('col.length')}</th>
            {actions && <th />}
          </tr>
        </thead>
        <tbody>
          {rows.map((m) => {
            const q = qualityLabel(m);
            return (
              <tr
                key={m.id}
                className={onOpen ? 'clickable' : ''}
                onClick={() => onOpen?.(m)}
                // Keyboard users open a row with Enter, like a click; keys from the row's own buttons are theirs.
                tabIndex={onOpen ? 0 : undefined}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.target === e.currentTarget) onOpen?.(m);
                }}
              >
                <td>
                  <TitleCell m={m} />
                </td>
                <td className="hide-sm file-td">
                  <div className="file-cell" title={m.fileName}>
                    {m.fileName}
                  </div>
                  <div className="cell-sub">{bytes(m.size)}</div>
                </td>
                <td className="quality-cell">
                  {q.main}
                  <small>{q.sub}</small>
                </td>
                <td>
                  <MediaStatusChip m={m} />
                </td>
                <td className="mono" style={{ fontSize: 12.5 }}>
                  {m.duration ? clock(m.duration) : '—'}
                </td>
                {actions && (
                  <td onClick={(e) => e.stopPropagation()} style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                    {actions(m)}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
